export type TranslationFunction = (
  path: string,
  opts?: { params?: Record<string, string> },
) => string;

declare const __DEV__: boolean | undefined;

// dot-path access into nested JSON — replaces `object-path` dep
function get(obj: Record<string, unknown>, path: string): string {
  // A caller can reach here with a non-string when a key is looked up in a
  // partial map (`t(labelKeyMap[type])` with an unmapped type). Splitting
  // `undefined` threw, and because `t` runs during render the whole screen
  // went to the ErrorBoundary. A missing translation must degrade to no text,
  // never take down the surface that asked for it.
  if (typeof path !== "string") {
    // Loud in development, silent in production. Returning "" stops the crash,
    // but swallowing it entirely would hide the caller's bug — the objection
    // raised by risk-assessment on #2321, and a fair one: a guard that only
    // degrades quietly is indistinguishable from correct behaviour.
    if (__DEV__) {
      console.warn(
        "[i18n] t() received a non-string path; rendering no text. " +
          "This usually means a partial lookup map returned undefined.",
        path,
      );
    }
    return "";
  }

  const value = path
    .split(".")
    .reduce<unknown>(
      (acc, key) =>
        acc && typeof acc === "object"
          ? (acc as Record<string, unknown>)[key]
          : undefined,
      obj,
    );
  return typeof value === "string" ? value : path;
}

function inject(str: string, params: Record<string, string>): string {
  return str.replace(/\$\{(.*?)\}/g, (_, key) => params[key] ?? "");
}

export function makeTFunc(
  json: object,
  opts?: { namespace?: string },
): TranslationFunction {
  return (path, { params } = {}) => {
    const fullPath = opts?.namespace ? `${opts.namespace}.${path}` : path;
    const value = get(json as Record<string, unknown>, fullPath);
    return params ? inject(value, params) : value;
  };
}
